/**
 * `stripInFlightPlaceholderNodes` — remove in-flight upload placeholder
 * nodes from a TipTap JSON doc before it is persisted anywhere.
 *
 * The serializer invariant behind the WIKI DOC drop-to-upload flow —
 * `DocPage` is the only caller that strips before persisting, so this is
 * not enforced on the card-description save path today. `docBodiesEqual`
 * also applies it when comparing bodies, which means placeholder churn is
 * discounted from equality on every rich-text surface. While
 * an upload runs, the editor holds a skeleton node carrying transient
 * attrs (`uploadingBlobUrl` on the optimistic image insert,
 * `uploadingPlaceholderId` on the video / file placeholders). Those nodes
 * reference revoked-on-resolve blob URLs and uploads that may never
 * complete — they must never reach the BE draft, a version-history row,
 * or IndexedDB (a mid-upload reload restoring a dead skeleton is exactly
 * the bug this guards against). The wiki richtext tier validates node
 * NAMES only and deliberately allows extra attrs, so a leaked transient
 * attr would silently persist rather than 400 — this strip is the guard.
 *
 * A node is an in-flight placeholder when:
 *   - `attrs.uploadingPlaceholderId != null` (video / file skeletons), OR
 *   - `type === 'image'` with `attrs.uploadingBlobUrl != null` AND
 *     `attrs.attachmentId == null` (optimistic image still uploading —
 *     once the attachment id lands the node is durable and stays).
 *
 * A paragraph emptied by the removal goes with it — an inline chip is
 * inserted wrapped in its own paragraph, and stripping the chip alone
 * would leave a stray blank line in the persisted draft. Paragraphs that
 * were ALREADY empty before the strip are untouched.
 *
 * Pure: returns a NEW object on every call and never mutates the input.
 */

import type { TipTapJsonContent } from '@/api/types-bridge'

interface TipTapJsonNode {
  type?: string
  attrs?: Record<string, unknown>
  content?: TipTapJsonNode[]
  [key: string]: unknown
}

function isInFlightPlaceholder(node: TipTapJsonNode): boolean {
  const attrs = node.attrs
  if (!attrs) return false
  if (attrs.uploadingPlaceholderId != null) return true
  if (node.type === 'image' && attrs.uploadingBlobUrl != null && attrs.attachmentId == null) {
    return true
  }
  return false
}

/**
 * Strip one node's subtree. Returns `null` when the node itself must be
 * dropped (it is a placeholder, or a paragraph the strip emptied out).
 */
function stripNode(node: TipTapJsonNode): TipTapJsonNode | null {
  if (isInFlightPlaceholder(node)) return null
  if (!Array.isArray(node.content)) return { ...node }

  const hadChildren = node.content.length > 0
  const nextContent: TipTapJsonNode[] = []
  let removedAny = false
  for (const child of node.content) {
    const stripped = stripNode(child)
    if (stripped === null) {
      removedAny = true
      continue
    }
    nextContent.push(stripped)
  }

  // Drop a paragraph the strip emptied — but never one that was empty to
  // begin with (an intentional blank line the user typed).
  if (node.type === 'paragraph' && hadChildren && removedAny && nextContent.length === 0) {
    return null
  }

  return { ...node, content: nextContent }
}

export function stripInFlightPlaceholderNodes(json: TipTapJsonContent): TipTapJsonContent {
  const doc = json as TipTapJsonNode
  const stripped = stripNode(doc)
  // The document node itself is never a placeholder, but stay defensive:
  // a null result collapses to an empty object rather than throwing.
  return (stripped ?? {}) as TipTapJsonContent
}
